import { Component, OnInit } from '@angular/core';
import { BackButton } from '../../shared/buttons/back-button';
import { Popup } from '../../../core/shared/components/popup/popup';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AppIcons } from '../../../core/icons/fa-icons';
import { faEye, faUsers, faCheck } from '@fortawesome/free-solid-svg-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { OnboradingService } from '../shared/service/onboarding.service';
import { Setup } from '../../setup/shared/service/setup';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { TableAction } from '../../shared/models/table-models';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { LeadService } from '../../leads/shared/service/leads.service';
import { applyDesignationFieldState } from '../shared/committee-person-fields';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-committee-members',
  imports: [CommonModule, FontAwesomeModule, Popup, BackButton, TranslatePipe],
  templateUrl: './committee-members.html',
  styleUrl: './committee-members.css',
})
export class CommitteeMembers implements OnInit {
  icons = AppIcons;

  // ================= ROUTE PARAMS =================
  crptOnbrdngSeq: number | null = null;
  clntSeq: number | null = null;
  isViewMode = false;
  readonly COMMITTEE_MEMBER_PERSON_TYPE = 4;

  readonly viewIcon = faEye;
  readonly selectMembersIcon = faUsers;
  readonly checkIcon = faCheck;

  isPopupViewMode = false;

  get isRelationshipOfficer(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem('user_role') || '').trim().toUpperCase() === 'LNLY_RO'
    );
  }
  // ================= TABLE =================
  list: any[] = [];

  /** cmtSeq of the currently expanded committee row — only one expanded at a time. */
  expandedCmtSeq: number | null = null;

  // ================= DELETE COMMITTEE POPUP =================
  showDeletePopup = false;
  deleteMessage = '';
  private committeeToDelete: any = null;

  // ================= REMOVE MEMBER POPUP =================
  showRemoveMemberPopup = false;
  removeMemberMessage = '';
  private memberToRemove: { committeeRow: any; member: any } | null = null;

  // ================= SELECT MEMBERS POPUP =================
  showSelectMembersPopup = false;
  selectedCommitteeRow: any = null;
  membersList: any[] = [];
  membersLoading = false;
  selectedMemberPrsnSeqs: number[] = [];
  membersDropdownOpen = false;

  /** Members already in the selected committee's child table — hidden from the dropdown. */
  private assignedMemberPrsnSeqs: number[] = [];

  // ================= POPUP =================
  showPopup = false;
  isEditMode = false;
  formData: any = {};
  fields: DynamicFieldConfig[] = [];

  /** Members already assigned to the committee being edited — preserved so editing the name doesn't wipe them. */
  private editingPrsnSeqs: number[] = [];

  // ================= COMMITTEE MEMBER ONLY POPUP =================
  // Reuses the exact Add popup (fields, validation, payload, endpoint) from
  // the Key Management component — kept under its own state so it doesn't
  // collide with the "Add Committee" popup above.
  readonly LIFETIME_CNIC_DATE = '3000-01-01';

  // ================= CNIC / PASSPORT DOCUMENT =================
  private readonly CNIC_PASSPORT_DOC_TYP = 39;
  private readonly CNIC_PASSPORT_ENTY_TYP = 16;
  showMemberOnlyPopup = false;
  isMemberOnlyPopupViewMode = false;
  memberOnlyFormData: any = {};
  memberOnlyFields: DynamicFieldConfig[] = [];

  memberOnlyCountryOptions: any[] = [];
  memberOnlyNationalityOptions: any[] = [];
  memberOnlyDepartmentOptions: any[] = [];
  memberOnlyDesignationOptions: any[] = [];
  memberOnlyQualificationOptions: any[] = [];
  memberOnlyCommitteeOptions: any[] = [];

  private memberOnlyLastCntrySeq = 0;
  private memberOnlyLastStSeq = 0;
  private memberOnlyLastDstrctSeq = 0;
  private memberOnlyLastThslSeq = 0;
  private memberOnlyLastNtnltySeq = 0;
  private memberOnlyLastDsgntnSeq = 0;

  // ================= NATIONALITY BASED CNIC/PASSPORT VALIDATION =================
  private readonly PAKISTANI_REFCD_SEQ = 2685;
  private readonly CNIC_FIELD_KEYS = ['cnc', 'cncIssuDt', 'cncExpryDt', 'isCnicLifetime'];
  private readonly PASSPORT_FIELD_KEYS = ['psprt', 'psprtIssuDt', 'psprtExpryDt'];

  constructor(
    private onboarding: OnboradingService,
    private setup: Setup,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private pickListService: PickListService,
    private leadService: LeadService,
    private translate: TranslateService,
  ) { }

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) this.crptOnbrdngSeq = +id;
    });

    this.route.queryParamMap.subscribe((params) => {
      const clientSeq = params.get('clientSeq');
      if (clientSeq) this.clntSeq = +clientSeq;
      this.isViewMode = params.get('mode') === 'view';
      this.buildActions();
    });

    this.buildFields();

    this.loadMemberOnlyCountries();
    this.loadMemberOnlyNationalities();
    this.loadMemberOnlyDepartments();
    this.loadMemberOnlyDesignations();
    this.loadMemberOnlyQualifications();
    this.loadMemberOnlyCommittees();

    if (this.crptOnbrdngSeq) {
      this.loadData();
    }
  }

  // ================= FIELDS =================
  buildFields() {
    this.fields = [
      { key: 'cmtSeq', label: 'ID', type: 'text', hidden: true },
      {
        key: 'cmtNm',
        label: 'ONB_COMMITTEE.COMMITTEE_NAME',
        type: 'text',
        validations: { minLength: 2, maxLength: 150, required: true },
      },
    ];
  }

  // ================= EXPANDABLE ROWS =================
  toggleCommitteeRow(row: any): void {
    this.expandedCmtSeq = this.expandedCmtSeq === row.cmtSeq ? null : row.cmtSeq;
  }

  isCommitteeExpanded(row: any): boolean {
    return this.expandedCmtSeq === row.cmtSeq;
  }

  isActionVisible(action: TableAction, row: any): boolean {
    const isVisible = action.visible ? action.visible(row) : true;
    const isHidden = action.hide ? action.hide(row) : false;
    return isVisible && !isHidden;
  }

  openRemoveMemberPopup(committeeRow: any, member: any): void {
    if (this.isViewMode) return;

    this.memberToRemove = { committeeRow, member };
    this.removeMemberMessage = 'ONB_COMMITTEE.REMOVE_MEMBER_CONFIRM';
    this.showRemoveMemberPopup = true;
  }

  confirmRemoveMember(): void {
    if (!this.memberToRemove) return;

    const { committeeRow, member } = this.memberToRemove;

    // Same payload shape as saveCommitteeMembers (handleSave / confirmDeleteCommittee) —
    // the deleted member's seq goes in prsnSeqs with delFlg = 1.
    const payload = {
      cmtSeq: Number(committeeRow.cmtSeq) || 0,
      cmtNm: committeeRow.cmtNm ?? '',
      crptOnbrdngSeq: Number(this.crptOnbrdngSeq) || 0,
      prsnSeqs: [Number(member.prsnSeq)],
      delFlg: 1,
    };

    this.onboarding.saveCommitteeMembers(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('ONB_COMMITTEE.MEMBER_REMOVED_SUCCESS'));
        this.memberToRemove = null;
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONB_COMMITTEE.FAILED_TO_REMOVE_MEMBER');
        this.toastr.error(msg);
      },
    });
  }

  // ================= LOAD DATA =================
  loadData() {
    if (!this.crptOnbrdngSeq) return;

    this.onboarding.getAllCommittees(this.crptOnbrdngSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        this.list = data;
      },
      error: () => {
        this.list = [];
        this.toastr.error(this.translate.instant('ONB_COMMITTEE.FAILED_TO_LOAD_COMMITTEES'));
      },
    });
  }

  onPopupFormChange(form: any): void {
    if (!form || this.isPopupViewMode) {
      return;
    }

    this.formData = {
      ...this.formData,
      ...form,
    };
  }

  // ================= POPUP =================
  openPopup(data?: any, viewOnly = false): void {
    // Full page view mode mein sirf View popup allow hoga.
    if (this.isViewMode && !viewOnly) {
      return;
    }

    this.isPopupViewMode = viewOnly;

    if (data) {
      this.isEditMode = !viewOnly;

      this.editingPrsnSeqs = Array.isArray(data.members)
        ? data.members.map((member: any) => Number(member.prsnSeq))
        : [];

      this.formData = {
        cmtSeq: data.cmtSeq ?? null,
        cmtNm: data.cmtNm ?? '',
      };
    } else {
      this.resetCommitteeMembersForm();
    }

    this.fields = this.fields.map((field) => ({
      ...field,
      disabled: this.isPopupViewMode,
    }));

    this.showPopup = true;
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    if (this.isViewMode || this.isPopupViewMode) {
      return;
    }

    if (!this.crptOnbrdngSeq) {
      this.toastr.error(this.translate.instant('ONBOARDING.ONBOARDING_ID_MISSING'));
      return;
    }

    const payload = {
      cmtSeq: form.cmtSeq ? Number(form.cmtSeq) : null,
      cmtNm: form.cmtNm ?? '',
      crptOnbrdngSeq: Number(this.crptOnbrdngSeq) || 0,
      prsnSeqs: this.isEditMode ? this.editingPrsnSeqs : [],
      delFlg: 0,
    };

    this.onboarding.saveCommitteeMembers(payload).subscribe({
      next: (res: any) => {
        if (res?.__hasError) return;

        const message = this.translate.instant(this.isEditMode ? 'ONB_COMMITTEE.COMMITTEE_UPDATED' : 'ONB_COMMITTEE.COMMITTEE_ADDED');

        this.toastr.success(message);

        this.showPopup = false;
        this.resetCommitteeMembersForm();
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONBOARDING.SAVE_FAILED');
        this.toastr.error(msg);
      },
    });
  };

  // ================= DELETE =================
  deleteCommitteeMember(row: any): void {
    if (this.isViewMode) return;

    this.committeeToDelete = row;
    this.deleteMessage = 'ONB_COMMITTEE.DELETE_COMMITTEE_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmDeleteCommittee(): void {
    if (!this.committeeToDelete) return;

    const row = this.committeeToDelete;

    const payload = {
      cmtSeq: Number(row.cmtSeq) || 0,
      cmtNm: row.cmtNm ?? '',
      crptOnbrdngSeq: Number(this.crptOnbrdngSeq) || 0,
      prsnSeqs: [],
      delFlg: 1,
    };

    this.onboarding.saveCommitteeMembers(payload).subscribe({
      next: (res: any) => {
        if (res?.__hasError) return;

        this.toastr.success(this.translate.instant('ONB_COMMITTEE.COMMITTEE_DELETED'));
        this.committeeToDelete = null;
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONBOARDING.DELETE_FAILED');
        this.toastr.error(msg);
      },
    });
  }

  /**
   * Only members created here through "+ Committee Member Only" (person type
   * 4) are edited from this screen; directors / key management members
   * assigned to a committee are edited on their own tabs.
   */
  canEditMember(member: any): boolean {
    return !this.isViewMode && Number(member?.prsnTyp) === this.COMMITTEE_MEMBER_PERSON_TYPE;
  }

  // ================= COMMITTEE MEMBER ONLY (reuses Key Management's Add popup) =================
  openMemberOnlyPopup(data?: any, viewOnly = false): void {
    if (this.isViewMode && !viewOnly) {
      return;
    }

    this.isMemberOnlyPopupViewMode = viewOnly;

    if (data) {
      const isCnicLifetime = Boolean(
        data.cncExpryDt && String(data.cncExpryDt).slice(0, 10) === this.LIFETIME_CNIC_DATE,
      );

      this.memberOnlyFormData = {
        prsnSeq: data.prsnSeq ?? null,
        flNm: data.flNm ?? '',
        fthrHsbndNm: data.fthrHsbndNm ?? '',
        cnc: data.cnc ?? '',
        cncIssuDt: data.cncIssuDt ?? null,
        isCnicLifetime,
        cncExpryDt: isCnicLifetime ? null : (data.cncExpryDt ?? null),
        psprt: data.psprt ?? '',
        psprtIssuDt: data.psprtIssuDt ?? null,
        psprtExpryDt: data.psprtExpryDt ?? null,
        dob: data.dob ?? null,
        ntnlty: data.ntnlty ?? 0,
        dptNm: data.dptNm ?? 0,
        hod: data.hod ?? '',
        dsgntn: data.dsgntn ?? 0,
        // null (not 0) when none is saved, so the required check still
        // stops a member saved before Qualification existed.
        qlfctn: Number(data.qlfctn) || null,
        crntPrfssnExp: data.crntPrfssnExp ?? '',
        shrhldng: null,
        rsdntlAddr: data.rsdntlAddr ?? '',
        cntrySeq: data.cntrySeq ?? 0,
        stSeq: data.stSeq ?? 0,
        dstrctSeq: data.dstrctSeq ?? 0,
        thslSeq: data.thslSeq ?? 0,
        citySeq: data.citySeq ?? 0,
        cnicPassportDoc: data.cnicPassportDoc ?? null,
      };

      this.memberOnlyLastCntrySeq = Number(data.cntrySeq) || 0;
      this.memberOnlyLastStSeq = Number(data.stSeq) || 0;
      this.memberOnlyLastDstrctSeq = Number(data.dstrctSeq) || 0;
      this.memberOnlyLastThslSeq = Number(data.thslSeq) || 0;
      this.memberOnlyLastNtnltySeq = Number(data.ntnlty) || 0;

      this.updateMemberOnlyCnicExpiryFieldState(isCnicLifetime);
      this.updateNationalityFieldState(this.isPakistanNationality(data.ntnlty));
      this.memberOnlyLastDsgntnSeq = Number(data.dsgntn) || 0;
      this.memberOnlyFields = applyDesignationFieldState(this.memberOnlyFields, data.dsgntn);
      this.preloadMemberOnlyGeoForView(data);
      this.loadCnicPassportDoc(Number(data.prsnSeq));
    } else {
      this.resetMemberOnlyForm();
    }

    this.showMemberOnlyPopup = true;
  }

  // Loads the state/district/tehsil/city option lists for the member being
  // viewed so the disabled selects still show the correct saved label.
  private preloadMemberOnlyGeoForView(data: any): void {
    if (data.cntrySeq) this.loadMemberOnlyStates(data.cntrySeq);
    if (data.stSeq) this.loadMemberOnlyDistricts(data.stSeq);
    if (data.dstrctSeq) this.loadMemberOnlyTehsils(data.dstrctSeq);
    if (data.thslSeq) this.loadMemberOnlyCities();
  }

  closeMemberOnlyPopup(): void {
    this.showMemberOnlyPopup = false;
  }

  loadMemberOnlyCountries(): void {
    this.setup.getCountries().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((c: any) => ({
          label: c.cntryNm,
          value: c.countrySeq,
        }));

        this.memberOnlyCountryOptions = options;

        this.buildMemberOnlyFields();
      },
      error: () => {
        this.toastr.error(this.translate.instant('ONBOARDING.FAILED_TO_LOAD_COUNTRIES'));
        this.buildMemberOnlyFields();
      },
    });
  }

  loadMemberOnlyNationalities(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.NATIONALITY]).subscribe(() => {
      this.memberOnlyNationalityOptions = this.pickListService.getOptionsSync(PKL_GRP.NATIONALITY);
      this.updateMemberOnlyFieldOptions('ntnlty', this.memberOnlyNationalityOptions);
    });
  }

  loadMemberOnlyDepartments(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.DEPARTMENT]).subscribe(() => {
      this.memberOnlyDepartmentOptions = this.pickListService.getOptionsSync(PKL_GRP.DEPARTMENT);
      this.updateMemberOnlyFieldOptions('dptNm', this.memberOnlyDepartmentOptions);
    });
  }

  loadMemberOnlyDesignations(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.DESIGNATION]).subscribe(() => {
      this.memberOnlyDesignationOptions = this.pickListService.getOptionsSync(PKL_GRP.DESIGNATION);
      this.updateMemberOnlyFieldOptions('dsgntn', this.memberOnlyDesignationOptions);
    });
  }

  loadMemberOnlyQualifications(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.QUALIFICATION]).subscribe(() => {
      this.memberOnlyQualificationOptions = this.pickListService.getOptionsSync(PKL_GRP.QUALIFICATION);
      this.updateMemberOnlyFieldOptions('qlfctn', this.memberOnlyQualificationOptions);
    });
  }

  // Reuses the same endpoint that populates the Committee Member table.
  loadMemberOnlyCommittees(): void {
    if (!this.crptOnbrdngSeq) return;

    this.onboarding.getAllCommittees(this.crptOnbrdngSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        this.memberOnlyCommitteeOptions = data.map((committee: any) => ({
          label: committee.cmtNm,
          value: committee.cmtSeq,
        }));
        this.updateMemberOnlyFieldOptions('cmtSeq', this.memberOnlyCommitteeOptions);
      },
      error: () => {
        this.memberOnlyCommitteeOptions = [];
        this.toastr.error(this.translate.instant('ONB_COMMITTEE.FAILED_TO_LOAD_COMMITTEES'));
      },
    });
  }

  buildMemberOnlyFields(): void {
    this.memberOnlyFields = [
      { key: 'prsnSeq', label: 'ID', type: 'text', hidden: true },
      {
        key: 'flNm',
        label: 'ONB_PERSON.FULL_NAME',
        type: 'text',
        validations: { minLength: 3, maxLength: 300, required: true },
      },
      {
        key: 'fthrHsbndNm',
        label: 'ONB_PERSON.FATHER_HUSBAND_NAME',
        type: 'text',
        validations: { minLength: 3, maxLength: 300, required: true },
      },
      {
        key: 'ntnlty',
        label: 'ONB_PERSON.NATIONALITY',
        type: 'select',
        options$: this.memberOnlyNationalityOptions,
        validations: { required: true },
      },
      {
        key: 'cnc',
        label: 'ONB_PERSON.CNIC',
        type: 'text',
        maxlength: 13,
        inputmode: 'numeric',
        validations: { required: true, cnic: true },
      },
      {
        key: 'cncIssuDt',
        label: 'ONB_PERSON.CNIC_ISSUE_DATE',
        type: 'date',
        validations: { required: true, noFuturDate: true },
      },
      {
        key: 'isCnicLifetime',
        label: 'ONB_PERSON.LIFETIME_CNIC',
        type: 'checkbox',
        value: false,
      },
      {
        key: 'cncExpryDt',
        label: 'ONB_PERSON.CNIC_EXPIRY_DATE',
        type: 'date',
        disabled: Boolean(this.memberOnlyFormData?.isCnicLifetime),
        validations: { required: true, futureDateOnly: true },
      },
      {
        key: 'psprt',
        label: 'ONB_PERSON.PASSPORT_NO',
        type: 'text',
        inputmode: 'Text',
        maxlength: 16,
        validations: {
          passport: true,
        },
      },
      {
        key: 'psprtIssuDt',
        label: 'ONB_PERSON.PASSPORT_ISSUE_DATE',
        type: 'date',
        validations: { noFuturDate: true },
      },
      {
        key: 'psprtExpryDt',
        label: 'ONB_PERSON.PASSPORT_EXPIRY_DATE',
        type: 'date',
        validations: { futureDateOnly: true },
      },
      {
        key: 'dob',
        label: 'ONB_PERSON.DATE_OF_BIRTH',
        type: 'date',
        validations: { required: true, minAge: 18 },
      },
      {
        key: 'dptNm',
        label: 'ONB_PERSON.DEPARTMENT_NAME',
        type: 'select',
        options$: this.memberOnlyDepartmentOptions,
        validations: { required: true },
      },
      {
        key: 'hod',
        label: 'ONB_PERSON.HEAD_OF_DEPARTMENT',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'dsgntn',
        label: 'ONB_PERSON.DESIGNATION',
        type: 'select',
        options$: this.memberOnlyDesignationOptions,
        validations: { required: true },
      },
      {
        key: 'qlfctn',
        label: 'ONB_PERSON.QUALIFICATION',
        type: 'select',
        options$: this.memberOnlyQualificationOptions,
        validations: { required: true },
      },
      {
        key: 'crntPrfssnExp',
        label: 'ONB_PERSON.CURRENT_PROFESSION_EXPERIENCE',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'cntrySeq',
        label: 'ONB_PERSON.COUNTRY',
        type: 'select',
        options$: this.memberOnlyCountryOptions,
        validations: { required: true },
      },
      {
        key: 'stSeq',
        label: 'ONB_PERSON.STATE_PROVINCE',
        type: 'select',
        options$: [],
        validations: { required: true },
      },
      {
        key: 'dstrctSeq',
        label: 'ONB_PERSON.DISTRICT',
        type: 'select',
        options$: [],
        validations: { required: true },
      },
      {
        key: 'thslSeq',
        label: 'ONB_PERSON.TEHSIL',
        type: 'select',
        options$: [],
        validations: { required: true },
      },
      {
        key: 'citySeq',
        label: 'ONB_PERSON.CITY',
        type: 'select',
        options$: [],
        validations: { required: true },
      },
      {
        key: 'rsdntlAddr',
        label: 'ONB_PERSON.RESIDENTIAL_ADDRESS',
        type: 'textarea',
        validations: { required: true },
      },
      {
        key: 'cnicPassportDoc',
        label: 'ONB_PERSON.CNIC_PASSPORT',
        type: 'file',
        accept:
          '.pdf,.doc,.docx,application/pdf,application/msword,' +
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        allowedFileTypes: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        validations: { required: true },
        errorMessages: { invalidFileType: 'ONB_PERSON.ONLY_PDF_WORD_ALLOWED' },
      },
    ];

    this.memberOnlyFields = applyDesignationFieldState(this.memberOnlyFields, this.memberOnlyFormData?.dsgntn);
  }

  // ================= NATIONALITY BASED CNIC/PASSPORT VALIDATION =================
  private isPakistanNationality(ntnltySeq: any): boolean {
    if (!ntnltySeq) {
      return false;
    }

    return Number(ntnltySeq) === this.PAKISTANI_REFCD_SEQ;
  }

  private updateNationalityFieldState(isPakistan: boolean): void {
    this.memberOnlyFields = this.memberOnlyFields.map((field) => {
      if (this.CNIC_FIELD_KEYS.includes(field.key)) {
        return {
          ...field,
          validations: { ...field.validations, required: isPakistan },
        };
      }

      if (this.PASSPORT_FIELD_KEYS.includes(field.key)) {
        return {
          ...field,
          validations: { ...field.validations, required: !isPakistan },
        };
      }

      return field;
    });
  }

  // ================= DOCUMENT HELPERS =================
  private static readonly DOC_EXT_BY_MIME: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  };

  /** Converts the file-upload field's data URL (base64) back into a binary File for the multipart payload. */
  private dataUrlToFile(dataUrl: any, fileName: string): File | null {
    const match = /^data:(.+?);base64,(.*)$/.exec(String(dataUrl ?? ''));

    if (!match) {
      return null;
    }

    const [, mimeType, base64] = match;
    const cleanBase64 = base64.replace(/\s/g, '');

    if (!cleanBase64) {
      return null;
    }

    const byteCharacters = atob(cleanBase64);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let index = 0; index < byteCharacters.length; index++) {
      byteArray[index] = byteCharacters.charCodeAt(index);
    }

    const extension = CommitteeMembers.DOC_EXT_BY_MIME[mimeType] || 'bin';

    return new File([byteArray], `${fileName}.${extension}`, { type: mimeType });
  }

  /** Guesses a stored document's mime type from its base64 signature (pdf / doc / docx only). */
  private detectDocMimeType(base64: string): string {
    const clean = base64.replace(/\s/g, '');

    if (clean.startsWith('JVBERi0')) {
      return 'application/pdf';
    }

    if (clean.startsWith('0M8R4KGxGuE')) {
      return 'application/msword';
    }

    if (clean.startsWith('UEsDB') || clean.startsWith('UEsBA') || clean.startsWith('UEsFB')) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }

    return 'application/octet-stream';
  }

  /** GET /leads/get-lead-docs-list/{leadSeq}/{entyTyp} — loads the member's uploaded CNIC/Passport doc for the edit/view popup. */
  private loadCnicPassportDoc(prsnSeq: number): void {
    if (!prsnSeq) {
      return;
    }

    this.leadService.getLeadDocs(prsnSeq, this.CNIC_PASSPORT_ENTY_TYP).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

        const doc = list.find(
          (item: any) => Number(item.docTyp) === this.CNIC_PASSPORT_DOC_TYP && !item.delFlg,
        );

        if (!doc?.docImg) {
          return;
        }

        const base64 = String(doc.docImg).trim();
        const mimeType = this.detectDocMimeType(base64);

        this.memberOnlyFormData = {
          ...this.memberOnlyFormData,
          cnicPassportDoc: `data:${mimeType};base64,${base64}`,
        };

        // Force DynamicFormComponent to rebuild from the updated model —
        // patching `memberOnlyFormData` alone isn't reliably picked up (see
        // updateMemberOnlyFieldOptions/updateNationalityFieldState above).
        this.memberOnlyFields = [...this.memberOnlyFields];
      },
      error: () => {
        this.toastr.error(this.translate.instant('ONBOARDING.FAILED_TO_LOAD_CNIC_PASSPORT_DOC'));
      },
    });
  }

  private updateMemberOnlyFieldOptions(key: string, options: any[]): void {
    const field = this.memberOnlyFields.find((f) => f.key === key);
    if (field) {
      field.options$ = options;
      this.memberOnlyFields = [...this.memberOnlyFields];
    }
  }

  private updateMemberOnlyCnicExpiryFieldState(isLifetime: boolean): void {
    this.memberOnlyFormData = {
      ...this.memberOnlyFormData,
      isCnicLifetime: isLifetime,
      ...(isLifetime ? { cncExpryDt: null } : {}),
    };

    this.memberOnlyFields = this.memberOnlyFields.map((field) => ({
      ...field,
      disabled:
        this.isMemberOnlyPopupViewMode ||
        (field.key === 'cncExpryDt' && isLifetime),
    }));
  }

  loadMemberOnlyStates(cntrySeq: number): void {
    if (!cntrySeq) {
      this.updateMemberOnlyFieldOptions('stSeq', []);
      return;
    }
    this.setup.getStates(cntrySeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((s: any) => ({
          label: s.stNm ?? s.name,
          value: s.stSeq ?? s.id,
        }));
        this.updateMemberOnlyFieldOptions('stSeq', options);
      },
      error: () => this.updateMemberOnlyFieldOptions('stSeq', []),
    });
  }

  loadMemberOnlyDistricts(stSeq: number): void {
    if (!stSeq) {
      this.updateMemberOnlyFieldOptions('dstrctSeq', []);
      return;
    }
    this.setup.getDistricts(stSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((d: any) => ({
          label: d.dstrctNm ?? d.name,
          value: d.dstrctSeq ?? d.id,
        }));
        this.updateMemberOnlyFieldOptions('dstrctSeq', options);
      },
      error: () => this.updateMemberOnlyFieldOptions('dstrctSeq', []),
    });
  }

  loadMemberOnlyTehsils(dstrctSeq: number): void {
    if (!dstrctSeq) {
      this.updateMemberOnlyFieldOptions('thslSeq', []);
      return;
    }
    this.setup.getTehsils(dstrctSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((t: any) => ({
          label: t.thslNm ?? t.name,
          value: t.thslSeq ?? t.id,
        }));
        this.updateMemberOnlyFieldOptions('thslSeq', options);
      },
      error: () => this.updateMemberOnlyFieldOptions('thslSeq', []),
    });
  }

  loadMemberOnlyCities(): void {
    this.setup.getCity().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((c: any) => ({
          label: c.ctyNm ?? c.cityNm ?? c.name,
          value: c.citySeq ?? c.cityId,
        }));
        this.updateMemberOnlyFieldOptions('citySeq', options);
      },
      error: () => this.updateMemberOnlyFieldOptions('citySeq', []),
    });
  }

  private capitalizeWords(value: any): any {
    if (typeof value !== 'string' || !value) {
      return value;
    }

    return value.replace(/(^|\s)(\S)/g, (_, space, char) => space + char.toUpperCase());
  }

  onMemberOnlyPopupFormChange(form: any): void {
    if (!form || this.isMemberOnlyPopupViewMode) {
      return;
    }

    const previousLifetimeValue = Boolean(this.memberOnlyFormData?.isCnicLifetime);

    this.memberOnlyFormData = {
      ...this.memberOnlyFormData,
      ...form,
      flNm: this.capitalizeWords(form.flNm),
      fthrHsbndNm: this.capitalizeWords(form.fthrHsbndNm),
    };

    const isCnicLifetime = Boolean(this.memberOnlyFormData.isCnicLifetime);

    if (isCnicLifetime !== previousLifetimeValue) {
      this.updateMemberOnlyCnicExpiryFieldState(isCnicLifetime);
    }

    const ntnltySeq = Number(form.ntnlty) || 0;

    if (ntnltySeq !== this.memberOnlyLastNtnltySeq) {
      this.memberOnlyLastNtnltySeq = ntnltySeq;
      this.updateNationalityFieldState(this.isPakistanNationality(ntnltySeq));
    }

    // CEO: Name of Department and Head Of Department are optional.
    const dsgntnSeq = Number(form.dsgntn) || 0;

    if (dsgntnSeq !== this.memberOnlyLastDsgntnSeq) {
      this.memberOnlyLastDsgntnSeq = dsgntnSeq;
      this.memberOnlyFields = applyDesignationFieldState(this.memberOnlyFields, dsgntnSeq);
    }

    const cntrySeq = Number(form.cntrySeq) || 0;
    const stSeq = Number(form.stSeq) || 0;
    const dstrctSeq = Number(form.dstrctSeq) || 0;
    const thslSeq = Number(form.thslSeq) || 0;

    if (cntrySeq !== this.memberOnlyLastCntrySeq) {
      this.memberOnlyLastCntrySeq = cntrySeq;
      this.memberOnlyLastStSeq = 0;
      this.memberOnlyLastDstrctSeq = 0;
      this.memberOnlyLastThslSeq = 0;

      this.loadMemberOnlyStates(cntrySeq);
      this.updateMemberOnlyFieldOptions('dstrctSeq', []);
      this.updateMemberOnlyFieldOptions('thslSeq', []);
      this.updateMemberOnlyFieldOptions('citySeq', []);
      return;
    }

    if (stSeq !== this.memberOnlyLastStSeq) {
      this.memberOnlyLastStSeq = stSeq;
      this.memberOnlyLastDstrctSeq = 0;
      this.memberOnlyLastThslSeq = 0;

      this.loadMemberOnlyDistricts(stSeq);
      this.updateMemberOnlyFieldOptions('thslSeq', []);
      this.updateMemberOnlyFieldOptions('citySeq', []);
      return;
    }

    if (dstrctSeq !== this.memberOnlyLastDstrctSeq) {
      this.memberOnlyLastDstrctSeq = dstrctSeq;
      this.memberOnlyLastThslSeq = 0;

      this.loadMemberOnlyTehsils(dstrctSeq);
      this.updateMemberOnlyFieldOptions('citySeq', []);
      return;
    }

    if (thslSeq !== this.memberOnlyLastThslSeq) {
      this.memberOnlyLastThslSeq = thslSeq;
      this.loadMemberOnlyCities();
    }
  }

  handleMemberOnlySave = (form: any) => {
    if (this.isViewMode || this.isMemberOnlyPopupViewMode) {
      return;
    }

    if (!this.crptOnbrdngSeq || !this.clntSeq) {
      this.toastr.error(this.translate.instant('ONBOARDING.ONBOARDING_OR_CLIENT_ID_MISSING'));
      return;
    }

    const isCnicLifetime = Boolean(
      form.isCnicLifetime ?? this.memberOnlyFormData.isCnicLifetime,
    );

    const cncExpryDt = isCnicLifetime
      ? this.LIFETIME_CNIC_DATE
      : (form.cncExpryDt ?? this.memberOnlyFormData.cncExpryDt ?? null);

    const formData = new FormData();

    if (form.prsnSeq) {
      formData.append('prsnSeq', String(Number(form.prsnSeq)));
    }

    formData.append('crptOnbrdngSeq', String(this.crptOnbrdngSeq));
    formData.append('clntSeq', String(this.clntSeq));
    formData.append('flNm', form.flNm ?? '');
    formData.append('fthrHsbndNm', form.fthrHsbndNm ?? '');

    if (form.cnc) {
      formData.append('cnc', String(Number(form.cnc)));
    }

    formData.append('cncIssuDt', form.cncIssuDt ?? '');
    formData.append('cncExpryDt', cncExpryDt ?? '');
    formData.append('psprt', form.psprt && form.psprt.trim() ? form.psprt.trim() : '');
    formData.append('psprtIssuDt', form.psprtIssuDt ?? '');
    formData.append('psprtExpryDt', form.psprtExpryDt ?? '');
    formData.append('prsnTyp', String(this.COMMITTEE_MEMBER_PERSON_TYPE));
    formData.append('dob', form.dob ?? '');
    formData.append('ntnlty', String(Number(form.ntnlty) || 0));
    formData.append('dptNm', String(Number(form.dptNm) || 0));
    formData.append('hod', form.hod ?? '');
    formData.append('dsgntn', String(Number(form.dsgntn) || 0));
    formData.append('qlfctn', String(Number(form.qlfctn) || 0));
    formData.append('crntPrfssnExp', form.crntPrfssnExp ?? '');
    formData.append('rsdntlAddr', form.rsdntlAddr ?? '');
    formData.append('cntrySeq', String(Number(form.cntrySeq) || 0));
    formData.append('stSeq', String(Number(form.stSeq) || 0));
    formData.append('dstrctSeq', String(Number(form.dstrctSeq) || 0));
    formData.append('thslSeq', String(Number(form.thslSeq) || 0));
    formData.append('citySeq', String(Number(form.citySeq) || 0));
    formData.append('delFlg', '0');

    const docFile = this.dataUrlToFile(form.cnicPassportDoc, 'cnic-passport');

    if (docFile) {
      formData.append('docs[0].docTyp', String(this.CNIC_PASSPORT_DOC_TYP));
      formData.append('docs[0].delFlg', 'false');
      formData.append('docs[0].docImg', docFile);
    }

    return this.onboarding.saveCorporatePerson(formData).subscribe({
      next: (res: any) => {
        if (res?.__hasError) return;

        this.toastr.success(
          this.translate.instant(
            form.prsnSeq
              ? 'ONB_COMMITTEE.COMMITTEE_MEMBER_UPDATED'
              : 'ONB_COMMITTEE.COMMITTEE_MEMBER_ADDED',
          ),
        );

        this.showMemberOnlyPopup = false;
        this.resetMemberOnlyForm();
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONBOARDING.SAVE_FAILED');
        this.toastr.error(msg);
      },
    });
  };

  private resetMemberOnlyForm(): void {
    this.memberOnlyFormData = {
      prsnSeq: null,
      flNm: '',
      fthrHsbndNm: '',
      cnc: '',
      cncIssuDt: null,
      isCnicLifetime: false,
      cncExpryDt: null,
      psprt: '',
      psprtIssuDt: null,
      psprtExpryDt: null,
      dob: null,
      ntnlty: null,
      dptNm: null,
      hod: '',
      dsgntn: null,
      qlfctn: null,
      crntPrfssnExp: '',
      shrhldng: null,
      rsdntlAddr: '',
      cntrySeq: null,
      stSeq: null,
      dstrctSeq: null,
      thslSeq: null,
      citySeq: null,
      cnicPassportDoc: null,
    };

    this.memberOnlyLastCntrySeq = 0;
    this.memberOnlyLastStSeq = 0;
    this.memberOnlyLastDstrctSeq = 0;
    this.memberOnlyLastThslSeq = 0;
    this.memberOnlyLastNtnltySeq = 0;

    this.updateMemberOnlyCnicExpiryFieldState(false);
    this.updateNationalityFieldState(true);
    this.memberOnlyLastDsgntnSeq = 0;
    this.memberOnlyFields = applyDesignationFieldState(this.memberOnlyFields, null);

    this.updateMemberOnlyFieldOptions('stSeq', []);
    this.updateMemberOnlyFieldOptions('dstrctSeq', []);
    this.updateMemberOnlyFieldOptions('thslSeq', []);
    this.updateMemberOnlyFieldOptions('citySeq', []);

    this.memberOnlyFormData = { ...this.memberOnlyFormData };
    this.memberOnlyFields = [...this.memberOnlyFields];
  }

  // ================= SELECT MEMBERS =================
  openSelectMembersPopup(row: any): void {
    this.selectedCommitteeRow = row;
    this.assignedMemberPrsnSeqs = (row?.members ?? []).map((m: any) => Number(m.prsnSeq));
    this.selectedMemberPrsnSeqs = [];
    this.membersList = [];
    this.membersDropdownOpen = false;
    this.showSelectMembersPopup = true;

    this.loadMembers(row?.crptOnbrdngSeq ?? this.crptOnbrdngSeq);
  }

  loadMembers(crptOnbrdngSeq: number | null): void {
    if (!crptOnbrdngSeq) return;

    this.membersLoading = true;

    this.onboarding.getAllCorporatePersons(crptOnbrdngSeq).subscribe({
      next: (res: any) => {
        this.membersList = Array.isArray(res) ? res : res?.data || [];
        this.membersLoading = false;
      },
      error: () => {
        this.membersList = [];
        this.membersLoading = false;
        this.toastr.error(this.translate.instant('ONB_COMMITTEE.FAILED_TO_LOAD_MEMBERS'));
      },
    });
  }

  /** Members not yet assigned to the committee — removed members reappear after loadData(). */
  get availableMembers(): any[] {
    return this.membersList.filter(
      (m: any) => !this.assignedMemberPrsnSeqs.includes(Number(m.prsnSeq)),
    );
  }

  isMemberPreSelected(member: any): boolean {
    return this.selectedMemberPrsnSeqs.includes(Number(member.prsnSeq));
  }

  get selectedMembersLabel(): string {
    if (!this.selectedMemberPrsnSeqs.length) return this.translate.instant('ONB_COMMITTEE.SELECT_MEMBERS_PLACEHOLDER');

    const names = this.membersList
      .filter((m: any) => this.selectedMemberPrsnSeqs.includes(Number(m.prsnSeq)))
      .map((m: any) => m.flNm);

    return names.length ? names.join(', ') : this.translate.instant('ONB_COMMITTEE.N_SELECTED', { count: this.selectedMemberPrsnSeqs.length });
  }

  toggleMembersDropdown(): void {
    this.membersDropdownOpen = !this.membersDropdownOpen;
  }

  toggleMemberSelection(member: any): void {
    const prsnSeq = Number(member.prsnSeq);

    this.selectedMemberPrsnSeqs = this.selectedMemberPrsnSeqs.includes(prsnSeq)
      ? this.selectedMemberPrsnSeqs.filter((seq) => seq !== prsnSeq)
      : [...this.selectedMemberPrsnSeqs, prsnSeq];
  }

  saveMembers(): void {
    if (!this.selectedCommitteeRow) return;

    const payload = {
      cmtSeq: Number(this.selectedCommitteeRow.cmtSeq) || 0,
      cmtNm: this.selectedCommitteeRow.cmtNm ?? '',
      crptOnbrdngSeq: Number(this.crptOnbrdngSeq) || 0,
      // Existing members are kept in the payload so they aren't dropped from the committee.
      prsnSeqs: [...this.assignedMemberPrsnSeqs, ...this.selectedMemberPrsnSeqs],
    };

    this.onboarding.saveCommitteeMembers(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('ONB_COMMITTEE.MEMBERS_ASSIGNED_SUCCESS'));
        this.closeSelectMembersPopup();
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONB_COMMITTEE.FAILED_TO_ASSIGN_MEMBERS');
        this.toastr.error(msg);
      },
    });
  }

  closeSelectMembersPopup(): void {
    this.showSelectMembersPopup = false;
    this.selectedCommitteeRow = null;
    this.membersList = [];
    this.selectedMemberPrsnSeqs = [];
    this.assignedMemberPrsnSeqs = [];
    this.membersDropdownOpen = false;
  }

  goToNext() {
    this.router.navigate(['/onboarding/documents', this.crptOnbrdngSeq], {
      queryParams: {
        clientSeq: this.clntSeq,
        ...(this.isViewMode ? { mode: 'view' } : {}),
      },
    });
  }

  // ================= ACTIONS =================

  actions: TableAction[] = [];

  buildActions(): void {
    const viewAction: TableAction = {
      icon: this.viewIcon,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    };

    if (this.isViewMode) {
      this.actions = [viewAction];
      return;
    }

    this.actions = [
      viewAction,
      {
        icon: this.icons.edit,
        tooltip: 'COMMON.EDIT',
        class: 'action-edit',
        onClick: (row) => this.openPopup(row, false),
      },
      {
        icon: this.icons.delete ?? this.icons.edit,
        tooltip: 'COMMON.DELETE',
        class: 'action-delete',
        onClick: (row) => this.deleteCommitteeMember(row),
      },
      {
        icon: this.selectMembersIcon,
        tooltip: 'ONB_COMMITTEE.SELECT_MEMBERS',
        class: 'action-select-members',
        onClick: (row) => this.openSelectMembersPopup(row),
      },
    ];
  }

  private resetCommitteeMembersForm(): void {
    this.isEditMode = false;
    this.isPopupViewMode = false;
    this.editingPrsnSeqs = [];

    this.formData = {
      cmtSeq: null,
      cmtNm: '',
    };

    // Dynamic form ko new references milengi
    this.fields = [...this.fields];
  }
}